import { motion } from "framer-motion";
import { getVisuals, TREND_ICON, CONF_ICON, TREND_ARROW } from "./treemap-utils";
import { Minus, Maximize2 } from "lucide-react";

const GAP = 4;

export function HeatmapTile({ tile, dimmed, onSelect, onHoverStart, onHoverEnd, compareMode, onCompareDragStart }) {
  const v = getVisuals(tile.state, tile.urgency);
  const TIcon = TREND_ICON[tile.trend] || Minus;
  const small = tile._w < 150 || tile._h < 110;
  const tiny = tile._w < 95 || tile._h < 75;
  const conf = tile.confidence || 0;
  const trendDur = tile.trend_duration || "";
  const trendShort = trendDur
    ? `${TREND_ARROW(tile.trend)} ${trendDur.replace(/^(Rising|Worsening|Improving|Stable) for /i, "")}`
    : "";

  return (
    <motion.div
      className={`absolute cursor-pointer overflow-hidden group ${v.pulse ? "tile-pulse" : ""}`}
      style={{
        left: tile._x + GAP, top: tile._y + GAP,
        width: tile._w - GAP * 2, height: tile._h - GAP * 2,
        background: v.bg, border: `1px solid ${v.border}`, borderRadius: 14,
        opacity: dimmed ? 0.2 : 1, boxShadow: v.glow,
        backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
      }}
      whileHover={{ scale: 1.04, zIndex: 20, boxShadow: v.glow.replace(/\d+px/, "60px") }}
      transition={{ type: "spring", stiffness: 400, damping: 28 }}
      onClick={() => onSelect(tile)}
      onHoverStart={() => onHoverStart(tile)}
      onHoverEnd={onHoverEnd}
      draggable={compareMode}
      onDragStart={(e) => {
        if (compareMode && onCompareDragStart) {
          e.dataTransfer?.setData("text/plain", tile.id);
          onCompareDragStart(tile);
        }
      }}
      data-testid={`tile-${tile.id}`}
    >
      {/* Expand icon — faint at rest, bright on hover */}
      {!tiny && (
        <span className="absolute top-2 left-2.5 text-white/[0.12] group-hover:text-white/50 transition-all duration-300" data-testid={`expand-${tile.id}`}>
          <Maximize2 className="w-3.5 h-3.5" />
        </span>
      )}

      {!tiny && (
        <span className="absolute top-2 right-2.5 text-[10px] text-white/20" data-testid={`conf-${tile.id}`}>
          {CONF_ICON(conf)}
        </span>
      )}
      <div className="h-full flex flex-col justify-center items-center text-center p-2">
        {!tiny && (
          <span className="text-[10px] uppercase tracking-[0.15em] text-white/40 mb-0.5 leading-none font-medium">
            {tile.title}
          </span>
        )}
        <span className={`font-bold leading-none ${small ? "text-lg" : "text-3xl"}`} style={{ color: v.accent }}>
          {tile.metric}
        </span>
        {!tiny && trendShort && (
          <div className="flex items-center gap-1 mt-1">
            <TIcon className="w-3 h-3 text-white/30" />
            <span className="text-[9px] leading-none text-white/30">{trendShort}</span>
          </div>
        )}
        {!tiny && !trendShort && (
          <div className="flex items-center gap-1 mt-1 opacity-30">
            <TIcon className="w-3 h-3" />
            <span className="text-[9px] leading-none">{tile.metric_context}</span>
          </div>
        )}
      </div>

      {/* Hover hint bar */}
      <div className="absolute bottom-0 inset-x-0 bg-white/[0.04] border-t border-white/[0.06] py-1 text-center opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
        <span className="text-[9px] text-white/40 uppercase tracking-widest font-medium">Click to explore</span>
      </div>
    </motion.div>
  );
}
